import { initializeApp, FirebaseApp } from 'firebase/app'
import { getAnalytics, Analytics, isSupported } from 'firebase/analytics'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as _firestorePkg from 'firebase/firestore'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { getFirestore } = _firestorePkg as any
import {
  getAuth,
  Auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  updateProfile,
} from 'firebase/auth'
import type { FirebaseConfig } from '../types'


// Firebase configuration
const firebaseConfig: FirebaseConfig = {
  apiKey: "AIzaSyCXC31eWUyB2bby8gOx_T-S6X9xfXCCcXU",
  authDomain: "rock-ar.firebaseapp.com",
  projectId: "rock-ar",
  storageBucket: "rock-ar.firebasestorage.app",
  messagingSenderId: "940493440787",
  appId: "1:940493440787:web:2e4698c64e07b56d927ce6",
  measurementId: "G-XLNY2WYZL8"
};

// Firebase app instance
let firebaseApp: FirebaseApp | null = null
let analytics: Analytics | null = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let firestoreDb: any = null
let authInstance: Auth | null = null

export type { User }

export interface AuthAccountStatus {
  isSignedIn: boolean
  hasAccount: boolean
  user: User | null
  username: string | null
}

/**
 * Initialize Firebase app with configuration
 * Returns the Firebase app instance for use across the application
 */
export const initializeFirebase = async (): Promise<FirebaseApp> => {
  if (!firebaseApp) {
    firebaseApp = initializeApp(firebaseConfig)
  }
  return firebaseApp
}

/**
 * Initialize Firebase Analytics with consent management
 * Only initializes if analytics is supported in the current environment
 */
export const initializeAnalytics = async (consentGiven: boolean = false): Promise<Analytics | null> => {
  try {
    // Check if analytics is supported (not available in some environments like localhost)
    const supported = await isSupported()
    if (!supported) {
      console.warn('Firebase Analytics is not supported in this environment')
      return null
    }

    // Initialize Firebase app if not already done
    if (!firebaseApp) {
      firebaseApp = await initializeFirebase()
    }

    // Initialize analytics (gtag is now loaded via HTML script tag)
    if (!analytics) {
      analytics = getAnalytics(firebaseApp)
      console.log('📊 Firebase Analytics initialized and connected to gtag')
    }

    // Set consent mode based on user consent
    if (consentGiven) {
      console.log('✅ Firebase Analytics initialized with consent - collection enabled')
    } else {
      console.log('⚠️ Firebase Analytics initialized without consent - collection disabled until consent granted')
    }

    return analytics
  } catch (error) {
    console.error('❌ Failed to initialize Firebase Analytics:', error)
    return null
  }
}

/**
 * Get the current Firebase app instance
 */
export const getFirebaseApp = (): FirebaseApp | null => {
  return firebaseApp
}

/**
 * Get (or lazily initialize) a Firestore instance.
 * Safe to call after initializeFirebase() has run.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFirestoreDb = (): any => {
  if (!firestoreDb) {
    if (!firebaseApp) throw new Error('Firebase not initialized — call initializeFirebase() first')
    firestoreDb = getFirestore(firebaseApp)
  }
  return firestoreDb
}

/**
 * Get the current Analytics instance
 */
export const getFirebaseAnalytics = (): Analytics | null => {
  return analytics
}

/**
 * Get (or lazily initialize) the Firebase Auth instance.
 */
export const getFirebaseAuth = (): Auth => {
  if (!authInstance) {
    if (!firebaseApp) throw new Error('Firebase not initialized — call initializeFirebase() first')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    authInstance = getAuth(firebaseApp as any)
  }
  return authInstance
}

/** Sign up with email + password. Returns the new User. */
export const registerWithEmail = async (
  email: string,
  password: string,
  username: string
): Promise<User> => {
  const auth = getFirebaseAuth()
  const cred = await createUserWithEmailAndPassword(auth, email, password)
  await updateProfile(cred.user, { displayName: username })
  return cred.user
}

/** Sign in with email + password. Returns the User. */
export const loginWithEmail = async (email: string, password: string): Promise<User> => {
  const auth = getFirebaseAuth()
  const cred = await signInWithEmailAndPassword(auth, email, password)
  return cred.user
}

/** Sign out the current user. */
export const logoutUser = async (): Promise<void> => {
  const auth = getFirebaseAuth()
  await signOut(auth)
}

/** Subscribe to auth state changes. Returns unsubscribe fn. */
export const subscribeToAuthState = (
  callback: (user: User | null) => void
): (() => void) => {
  const auth = getFirebaseAuth()
  return onAuthStateChanged(auth, callback)
}

/**
 * Verify auth state and profile existence in Firestore (/users/{uid}).
 * This ensures users are both signed in and have an account document.
 */
export const getAuthAccountStatus = async (): Promise<AuthAccountStatus> => {
  await initializeFirebase()
  const auth = getFirebaseAuth()
  const user = auth.currentUser

  if (!user) {
    return {
      isSignedIn: false,
      hasAccount: false,
      user: null,
      username: null,
    }
  }

  const profile = await firestoreGetDoc('users', user.uid)
  const username =
    (profile?.username as string | undefined) ||
    user.displayName ||
    user.email?.split('@')[0] ||
    null

  return {
    isSignedIn: true,
    hasAccount: !!profile,
    user,
    username,
  }
}

/**
 * Throws when the user is not signed in or missing a /users profile document.
 */
export const requireAuthenticatedAccount = async (): Promise<{
  user: User
  username: string
}> => {
  const status = await getAuthAccountStatus()

  if (!status.user || !status.isSignedIn || !status.hasAccount) {
    const error = new Error('Account required') as Error & {
      code: 'auth/account-required'
    }
    error.code = 'auth/account-required'
    throw error
  }

  return {
    user: status.user,
    username: status.username || 'User',
  }
}

export const isAccountRequiredError = (err: unknown): boolean => {
  return (err as { code?: string })?.code === 'auth/account-required'
}

// ── Firestore helpers (avoids direct firebase/firestore imports in components) ──
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { doc: _doc, setDoc: _setDoc, getDoc: _getDoc, serverTimestamp: _serverTimestamp } = _firestorePkg as any

export const firestoreSetDoc = async (
  collection: string,
  id: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>
): Promise<void> => {
  const db = getFirestoreDb()
  await _setDoc(_doc(db, collection, id), data)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const firestoreGetDoc = async (collection: string, id: string): Promise<any | null> => {
  const db = getFirestoreDb()
  const snap = await _getDoc(_doc(db, collection, id))
  return snap.exists() ? snap.data() : null
}

export const firestoreServerTimestamp = (): unknown => _serverTimestamp()

